import { message } from '@/components/Message';
import { notification } from '@/components/Notification';
import { Button, Space } from 'antd';
import { enableMapSet, produce } from 'immer';
import React, { ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useNavigate } from 'react-router-dom';
import { useImmer } from 'use-immer';
import { addPOSChangeListener, removeWsListener, WsActions } from '../../../api/ws';
import { BaseInfoContext } from '@/context/BaseInfoContext';
import { Card, requestCardList } from '@/models/card';
import { Category, requestCategoryList } from '@/models/category';
import { InventoryItem } from '@/models/inventory';
import { PoiConfig } from '@/models/poiConfig';
import { PointRules, requestPointRules } from '@/models/point';
import { SKU, SKUSpecification, SKUWrapper, useAllGoods } from '@/models/sku';
import { requestOperatorList, Staff } from '@/models/staff';
import { goodsToSearchDoc, SearchGoodsFuncType } from '@/utils/search';
import { playSound } from '@/utils/sound';
import DataInvalidatedModal from './dataInvalidateModal';
import SyncUI, { SyncUIProp } from './sync';
enableMapSet();
let POS_DATA_VERSION: number;

export const getPosDataVersion = () => POS_DATA_VERSION;

let hasSetSearchIndex = false;

//   this.add({
//     "id": item.id,
//     "namePy": item.namePy,
//     "namePyShort": item.namePy.split(' ').map(str => str.substring(0, 1)).join('')
//   })

type Props = {
  children: ReactNode;
};

export const POSBaseInfoContext = React.createContext<{
  categoryList: Category[];
  pointRules: PointRules;
  cardList: Card[];
  getSkuInCategory: (categoryId: string | number) => SKUWrapper[];
  getSkuByGoodsSn: (sn: string) => SKUWrapper;
  getSkuByIds: (ids: number[]) => SKUWrapper[];
  addOrUpdateSkuWrapper: (skuWrapper: SKUWrapper) => void;
  addOrUpdateSkuWrapperFromInventoryItems: (inventoryItems: InventoryItem[]) => void;
  allGoodsMap: Map<number, SKU>;
  searchGoods: SearchGoodsFuncType;
  getSkuSpecificationById: (specificationId: number) => SKUSpecification;
  getOperatorList: () => Staff[];
}>({
  categoryList: [],
  pointRules: null,
  cardList: [],
  getSkuInCategory: () => [],
  getSkuByIds: () => [],
  allGoodsMap: new Map(),
  getSkuByGoodsSn: () => null,
  addOrUpdateSkuWrapper: () => {},
  addOrUpdateSkuWrapperFromInventoryItems: () => {},
  searchGoods: () => [],
  getOperatorList: () => [],
  getSkuSpecificationById: () => null,
});

export enum SyncStatus {
  // 未同步
  PENDING,
  // 同步中
  PROGRESSING,
  // 同步完成
  DONE,
  // 离开同步界面，进入系统
  EXIT,
}

const Synchronizer: React.FC<Props> = ({ children }: Props): React.ReactElement => {
  const navigate = useNavigate();
  const [ready, setReady] = useState<SyncStatus>(SyncStatus.PENDING);
  const [, updateBaseInfo] = useContext(BaseInfoContext);

  // 进入系统
  const enterSystem = () => {
    if (ready === SyncStatus.DONE) {
      navigate(`/POS/index`);
      playSound('click');
      setReady(SyncStatus.EXIT);
    }
  };

  useHotkeys(
    'enter',
    () => {
      enterSystem();
    },
    [ready]
  );

  const [messages, setMessage] = useImmer<SyncUIProp['messages']>([]);
  const [progressPercent, setProgressPercent] = useState(0);

  // 积分规则
  const { payload: pointRules, send: getPointRule } = requestPointRules();

  // 美容师
  const { payload: operatorList, send: getOperatorList } = requestOperatorList();

  // 预付卡
  const { payload: cardList, send: getCardList } = requestCardList();

  // 品类
  const { payload: categoryList, setPayload: setCategoryList, send: getAllCategory } = requestCategoryList();

  const {
    goodsMap,
    setGoodsMap,
    setSpecificationMap,
    setGoodsSnToGoodsIdMap,
    categoryIdGoodsIdMap,
    setCategoryIdGoodsIdMap,
    getSkuWrapperByGoodsSn,
    updateStock,
    getSKUWrapperByGoodsId: getSKUWrapperByGoodsIdOrigin,
    fetchData: fetchAllGoodsData,
    goodsSearchIndex,
    searchGoods,
    skuSpecificationMap,
  } = useAllGoods();

  const getSKUWrapperByGoodsId: typeof getSKUWrapperByGoodsIdOrigin = goodsId => {
    const skuWrapper = getSKUWrapperByGoodsIdOrigin(goodsId);
    if (skuWrapper) {
      // 门店下架
      if (skuWrapper.goods.isStoreOnSale === false) {
        return null;
      }
      return skuWrapper;
    }
  };

  useEffect(() => {
    if (goodsMap.size && !hasSetSearchIndex) {
      // 添加到搜索引擎
      hasSetSearchIndex = true;
      // goodsSearchIndex.setCollection(
      //   Array.from(goodsMap.entries()).map(([, item]) => goodsToSearchDoc(item))
      // );
    }
  }, [goodsMap]);

  // 发起数据请求
  useEffect(() => {
    if (ready === SyncStatus.PENDING) {
      const asyncTask = async function* () {
        let doneCount = 0;
        let totalCount = 5;

        const categoryPayload = await getAllCategory();
        if (categoryPayload && categoryPayload.list && categoryPayload.list.length) {
          yield {
            done: ++doneCount,
            total: totalCount,
            msg: `【品类】品类树拉取成功`,
          };

          await fetchAllGoodsData();
          yield {
            done: ++doneCount,
            total: totalCount,
            msg: `【商品】商品数据拉取成功`,
          };
        }

        await getPointRule();
        yield {
          done: ++doneCount,
          total: totalCount,
          msg: `【积分】规则拉取成功`,
        };

        await getCardList();
        yield {
          done: ++doneCount,
          total: totalCount,
          msg: `【预付卡】规则拉取成功`,
        };

        await getOperatorList();
        yield {
          done: ++doneCount,
          total: totalCount,
          msg: `【美容师】信息拉取成功`,
        };
      };

      setReady(SyncStatus.PROGRESSING);
      (async function () {
        for await (let result of asyncTask()) {
          setProgressPercent((result.done / result.total) * 100);
          setMessage(draft => {
            draft.push({ text: result.msg });
          });
        }
        setReady(SyncStatus.DONE);

        try {
          // 设置副屏图片
          window.__POS_Device__.setConsumerWecomQrImage(
            'https://cdn.il-canto.com/assets/images/bg/business_card_1.jpg?v1'
          );
        } catch (e) {}
      })();
    }
  }, [ready]);

  /**
   * 添加商品 skuWrapper
   * @param skuWrapper
   */
  const addOrUpdateSkuWrapper = useCallback(
    (skuWrapper: SKUWrapper) => {
      let goods = skuWrapper.goods;
      let existsGoods = goodsMap.get(goods.id);
      let specifications = skuWrapper.specifications;
      // add goods
      setGoodsMap(draft => {
        draft.set(goods.id, { ...goods });
      });

      // add sn->goods cache
      setGoodsSnToGoodsIdMap(draft => {
        if (!goods.isStoreOnSale) {
          // 下架
          draft.delete(goods.goodsSn);
        } else {
          // 上架
          draft.set(goods.goodsSn, goods.id);
        }
      });

      // add goods to category
      setCategoryIdGoodsIdMap(draft => {
        // 商品添加到了新的品类
        if (!draft[goods.categoryId]) {
          draft[goods.categoryId] = new Set();
        }

        if (existsGoods) {
          // 先从旧的品类删除
          draft[existsGoods.categoryId].delete(existsGoods.id);
        }
        draft[goods.categoryId].add(goods.id);
      });

      // add specification
      setSpecificationMap(draft => {
        draft[goods.id] = specifications;
      });

      // 搜索索引更新
      goodsSearchIndex.remove(item => item.id === goods.id);
      goodsSearchIndex.add(goodsToSearchDoc(goods));
    },
    [setGoodsMap, goodsMap, setGoodsSnToGoodsIdMap, setCategoryIdGoodsIdMap, setSpecificationMap]
  );

  /**
   * 删除商品
   */
  const deleteGoodsById = useCallback(
    (id: number) => {
      const goods = goodsMap.get(id);
      if (goods) {
        setGoodsMap(draft => {
          draft.delete(id);
        });
        setGoodsSnToGoodsIdMap(draft => {
          draft.delete(goods.goodsSn);
        });
        setCategoryIdGoodsIdMap(draft => {
          if (draft[goods.categoryId]) {
            draft[goods.categoryId].delete(goods.id);
          }
        });
        setSpecificationMap(draft => {
          delete draft[goods.id];
        });

        // 搜索索引更新
        goodsSearchIndex.remove(item => item.id === id);
        return goods;
      }
    },
    [goodsMap, setGoodsMap, setGoodsSnToGoodsIdMap, setCategoryIdGoodsIdMap, setSpecificationMap]
  );

  const resetAndSync = () => {
    setReady(SyncStatus.PENDING);
    // 重置数据
    hasSetSearchIndex = false;
    goodsSearchIndex.setCollection([]);
    setGoodsMap(() => new Map<number, SKU>());
    setSpecificationMap(() => ({}));
    setGoodsSnToGoodsIdMap(() => new Map());
    setCategoryIdGoodsIdMap(() => ({}));
    setMessage(() => []);
    setProgressPercent(0);
  };

  const notifyPOSDataChange = (modifier: string) => {
    const key = `global-notification-pos-data-change`;
    notification.info({
      duration: null,
      message: `POS机数据变更通知`,
      description: (
        <>
          <div>管理员{modifier ? ` ${modifier} ` : ''}修改了经营数据</div>
          <div>是否立即进行数据同步?</div>
        </>
      ),
      key,
      actions: (
        <Space>
          <Button
            size={'large'}
            onClick={() => {
              notification.destroy(key);
            }}>
            暂不处理
          </Button>
          <Button
            size={'large'}
            type={'primary'}
            onClick={() => {
              resetAndSync();
              notification.destroy(key);
            }}>
            现在同步
          </Button>
        </Space>
      ),
    });
  };

  const [posDataVersionLatest, setPosDataVersionLatest] = useState(-1);

  // WS 指令: 商城数据修改
  useEffect(() => {
    const id = addPOSChangeListener<unknown>(({ modifier, payload, time, version }, domain, action) => {
      if (!isNaN(version)) {
        POS_DATA_VERSION = version;
        console.log(`数据版本更新: ${version}`);
      }

      switch (action) {
        // 数据版本 (在 ws onopen 时接收)
        case WsActions.POS_DATA_VERSION:
          let version = (payload as { version: number }).version;
          if (!POS_DATA_VERSION) {
            POS_DATA_VERSION = version;
            console.log(`初始化数据版本: ${version}`);
          } else {
            if (POS_DATA_VERSION < version) {
              // 数据版本过期
              setPosDataVersionLatest(version);
            }
          }
          break;

        // 库存
        case WsActions.STOCK_UPDATE: {
          updateStock(payload as { [goodsIdAndSpecificationId: string]: number });
          break;
        }

        // 商品: 新增 / 修改
        case WsActions.GOODS_UPDATE_ALL:
        case WsActions.GOODS_ADD: {
          let skuWrapper = payload as SKUWrapper;
          if (skuWrapper && skuWrapper.goods && skuWrapper.goods.id) {
            addOrUpdateSkuWrapper(skuWrapper);
            message.info(`商品 "${skuWrapper.goods.name}" 信息有更新, 请留意`);
          }
          break;
        }

        // 商品删除
        case WsActions.GOODS_DELETE: {
          const goodsId = payload as number;
          const deletedGoods = deleteGoodsById(goodsId);
          if (deletedGoods) {
            message.info(`商品 "${deletedGoods.name}" 被删除, 请留意`);
          }
          break;
        }
        //
        case WsActions.CATEGORY_ADD:
        case WsActions.CATEGORY_UPDATE: {
          let category = payload as Category;
          setCategoryList(draft => {
            const found = draft.list.findIndex(cate => cate.id === category.id);
            if (found > -1) {
              // delete
              draft.list.splice(found, 1);
            }
            draft.list.push(category);
          });
          break;
        }
        case WsActions.CATEGORY_DELETE: {
          let categoryId = payload as number;
          setCategoryList(draft => {
            const found = draft.list.findIndex(cate => cate.id === categoryId);
            if (found > -1) {
              // delete
              draft.list.splice(found, 1);
            }
          });
          break;
        }
        case WsActions.CATEGORY_ORDER_CHANGE: {
          let orderMap = payload as { [id: string]: number };
          setCategoryList(draft => {
            draft.list.forEach(category => {
              category.sortOrder = orderMap[category.id] || category.sortOrder;
            });
          });
          break;
        }
        case WsActions.CARD_ADD:
        case WsActions.CARD_UPDATE:
        case WsActions.CARD_DELETE: {
          // 预付卡: 全量更新
          getCardList().then();
          break;
        }
        case WsActions.POINT_RULE_CHANGE: {
          // 积分规则: 全量更新
          getPointRule().then();
          break;
        }
        case WsActions.POI_CONFIG_UPDATE: {
          // 商城配置 + 充值规则
          let poiConfig = payload as PoiConfig;
          updateBaseInfo({ poiConfig });
          break;
        }
        default:
          notifyPOSDataChange(modifier);
      }
    });

    return () => {
      removeWsListener(id);
    };
  }, [addOrUpdateSkuWrapper, deleteGoodsById]);

  return (
    <>
      <DataInvalidatedModal
        posDataVersionLatest={posDataVersionLatest}
        onRequestIgnore={() => {
          POS_DATA_VERSION = posDataVersionLatest;
          setPosDataVersionLatest(-1);
        }}
        onRequestSync={() => {
          POS_DATA_VERSION = posDataVersionLatest;
          setPosDataVersionLatest(-1);
          resetAndSync();
        }}
      />
      {ready === SyncStatus.EXIT ? (
        <POSBaseInfoContext.Provider
          value={{
            searchGoods,
            addOrUpdateSkuWrapperFromInventoryItems: (inventoryItems = [] as InventoryItem[]) => {
              for (let { goods, updateSpecification } of inventoryItems) {
                if (goods && updateSpecification) {
                  addOrUpdateSkuWrapper({
                    goods,
                    specifications: [{ ...updateSpecification }],
                  });
                }
              }
            },
            allGoodsMap: goodsMap,
            cardList: produce(cardList, draft => {
              if (draft) {
                return draft.filter(card => card.isOnSale);
              }
              return draft;
            }),
            categoryList: produce(categoryList.list, draft => {
              draft.push({
                id: 0,
                name: '默认品类',
              } as Category);
            })
              .filter(category => {
                return categoryIdGoodsIdMap[category.id] && categoryIdGoodsIdMap[category.id].size > 0;
              })
              .sort((cate1, cate2) => cate1.sortOrder - cate2.sortOrder),
            pointRules,
            getSkuByGoodsSn: getSkuWrapperByGoodsSn,
            addOrUpdateSkuWrapper,
            getSkuByIds: ids => {
              const data: SKUWrapper[] = [];
              for (let goodsId of ids) {
                const skuWrapper = getSKUWrapperByGoodsId(goodsId);
                if (skuWrapper) {
                  data.push(skuWrapper);
                }
              }
              return data;
            },
            getSkuInCategory: categoryId => {
              const data: SKUWrapper[] = [];
              const goodsIds = categoryIdGoodsIdMap[+categoryId];
              for (let goodsId of goodsIds) {
                const skuWrapper = getSKUWrapperByGoodsId(goodsId);
                if (skuWrapper) {
                  data.push(skuWrapper);
                }
              }

              // TODO: 优化排序算法: 应该在写入的时候对 goodsIds 进行排序
              data.sort((a, b) => (a.goods.sortOrder < b.goods.sortOrder ? 1 : -1));

              // Object.entries(cachedSkuWithSpecifications)
              //   .filter(
              //     ([goodsId, skuWrapper]) => skuIds.has(+goodsId) && skuWrapper.goods.isStoreOnSale
              //   )
              //   .map(([, goods]) => {
              //     data.push(goods);
              //   });
              return data;
            },
            getSkuSpecificationById: (id: number) => {
              return skuSpecificationMap[id];
            },
            getOperatorList: () => operatorList,
          }}>
          {children}
        </POSBaseInfoContext.Provider>
      ) : (
        <SyncUI
          progressPercent={progressPercent}
          messages={messages}
          ready={ready}
          onRequestEnterSystem={enterSystem}
        />
      )}
    </>
  );
};

export default Synchronizer;
